import React, { useEffect, useState } from "react";
import { Ico } from "./primitives.jsx";
import { Field } from "./auth.jsx";
import { apiGet, apiPost, apiPatch, ApiError, navigate } from "./api.jsx";

function Notice({ tone, children }) {
  const palette = tone === "error"
    ? { border: "var(--bad)", bg: "color-mix(in srgb, var(--bad) 10%, transparent)", color: "var(--bad)" }
    : { border: "var(--accent-hi)", bg: "var(--accent-soft)", color: "var(--text)" };
  return (
    <div style={{
      marginBottom: 18,
      padding: "12px 14px",
      border: `1px solid ${palette.border}`,
      borderRadius: 4,
      background: palette.bg,
      fontSize: 13,
      color: palette.color,
    }}>{children}</div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label style={{ display: "block" }}>
      <div className="h-eyebrow" style={{ marginBottom: 6 }}>{label}</div>
      <select
        value={value || ""}
        onChange={onChange}
        style={{
          width: "100%",
          boxSizing: "border-box",
          border: "1px solid var(--line-strong)",
          borderRadius: 4,
          padding: "10px 12px",
          background: "var(--surface)",
          fontSize: 14,
          color: "var(--text)",
          outline: "none",
        }}
      >
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

const COMPANY_TYPE_OPTIONS = [
  { v: "", l: "Pick a category…" },
  { v: "device", l: "Medical device" },
  { v: "diagnostic", l: "Diagnostic" },
  { v: "therapeutic", l: "Therapeutic" },
  { v: "digital_health", l: "Digital health" },
  { v: "other", l: "Something else" },
];

const ACCOUNT_TYPE_LABEL = {
  company: "Company",
  investor: "Investor",
  administrator: "Administrator",
};

export function AccountSettings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiGet("/me/profile/");
        setForm(data);
      } catch (err) {
        setError(err instanceof ApiError ? (err.body?.error || err.message) : "Couldn't load your settings.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function update(k) {
    return (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  }

  async function save(e) {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await apiPatch("/me/profile/", {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        user_title: form.user_title,
        company_name: form.company_name,
        company_type: form.company_type,
      });
      setForm(updated);
      setNotice(updated.verification_sent
        ? "Settings updated. A verification email has been sent to your new email address."
        : "Settings updated.");
    } catch (err) {
      if (err instanceof ApiError && Array.isArray(err.body?.errors)) {
        setError(err.body.errors.join(" "));
      } else if (err instanceof ApiError) {
        setError(err.body?.error || err.message);
      } else {
        setError("Save failed. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="page route-enter">
        <div className="page-header">
          <div>
            <div className="h-eyebrow" style={{ marginBottom: 8 }}>Account</div>
            <h1 className="page-title">Settings.</h1>
          </div>
        </div>
        <div className="card" style={{ maxWidth: 760 }}>
          <p className="dim" style={{ fontSize: 13 }}>Loading your details…</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="page route-enter">
        <div className="page-header">
          <div>
            <div className="h-eyebrow" style={{ marginBottom: 8 }}>Account</div>
            <h1 className="page-title">Settings.</h1>
          </div>
        </div>
        <Notice tone="error">{error || "Couldn't load your settings."}</Notice>
      </div>
    );
  }

  const accountType = form.account_type || "company";
  const showCompanyCard = accountType === "company";
  const showInvestorCard = accountType === "investor";

  return (
    <div className="page route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{ marginBottom: 8 }}>Account</div>
          <h1 className="page-title">Settings.</h1>
          <p className="page-sub" style={{ marginTop: 10, maxWidth: 580 }}>
            Update your profile, keep your company details current, and manage how you sign in.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 760, display: "flex", flexDirection: "column", gap: 20 }}>
        {error && <Notice tone="error">{error}</Notice>}
        {notice && <Notice>{notice}</Notice>}

        <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="card card--flush">
            <div className="card-header">
              <div>
                <div className="eyebrow">Profile</div>
                <div className="title">Your name, contact email, and what you do.</div>
              </div>
            </div>
            <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field label="First name" value={form.first_name} onChange={update("first_name")} autoComplete="given-name" />
                <Field label="Last name" value={form.last_name} onChange={update("last_name")} autoComplete="family-name" />
              </div>
              <Field label="Work email" type="email" value={form.email} onChange={update("email")} required autoComplete="email" />
              <div className="dim" style={{ fontSize: 12, marginTop: -6 }}>
                Changing your email sends a verification link to the new address before the change takes effect.
              </div>
              <Field label="Title" value={form.user_title} onChange={update("user_title")} autoComplete="organization-title" />
            </div>
          </div>

          <div className="card card--flush">
            <div className="card-header">
              <div>
                <div className="eyebrow">Account type</div>
                <div className="title">Fixed at sign-up. Contact support if you need to change it.</div>
              </div>
              <span className="pill" style={{ fontSize: 11 }}>
                <i className="dot" />
                {ACCOUNT_TYPE_LABEL[accountType] || "User"}
              </span>
            </div>
          </div>

          {showCompanyCard && (
            <div className="card card--flush">
              <div className="card-header">
                <div>
                  <div className="eyebrow">Company</div>
                  <div className="title">Shown on submissions you share with investors.</div>
                </div>
              </div>
              <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Field label="Company name" value={form.company_name} onChange={update("company_name")} />
                <SelectField label="Category" value={form.company_type} onChange={update("company_type")} options={COMPANY_TYPE_OPTIONS} />
              </div>
            </div>
          )}

          {showInvestorCard && (
            <div className="card card--flush">
              <div className="card-header">
                <div>
                  <div className="eyebrow">Organization</div>
                  <div className="title">The fund or firm on your business card.</div>
                </div>
              </div>
              <div className="card-body">
                <Field label="Organization" value={form.company_name} onChange={update("company_name")} />
              </div>
            </div>
          )}

          <div className="row" style={{ gap: 10, justifyContent: "flex-end" }}>
            <button type="button" className="btn btn--ghost" onClick={() => navigate("dashboard")}>Cancel</button>
            <button type="submit" disabled={saving} className="btn btn--primary" style={{ opacity: saving ? 0.6 : 1 }}>
              {saving ? "Saving…" : "Save changes"} <Ico n="arrow" size={14} />
            </button>
          </div>
        </form>

        <div className="card card--flush">
          <div className="card-header">
            <div>
              <div className="eyebrow">Security</div>
              <div className="title">Manage how you sign in.</div>
            </div>
          </div>
          <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <SecurityRow
              heading="Password"
              body="Update the password you use to sign in."
              cta="Change password"
              onClick={() => navigate("change-password")}
            />
            <SecurityRow
              heading="Forgot your password?"
              body="Send a reset link to your email if you can't remember the current one."
              cta="Reset password"
              onClick={() => { location.href = "/password-reset-request/"; }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function SecurityRow({ heading, body, cta, onClick }) {
  return (
    <div className="row" style={{
      padding: "12px 0",
      borderTop: "1px solid var(--line)",
      gap: 16,
      alignItems: "center",
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{heading}</div>
        <div className="dim" style={{ fontSize: 13, marginTop: 2 }}>{body}</div>
      </div>
      <button type="button" className="btn btn--ghost" onClick={onClick}>
        {cta} <Ico n="arrow" size={12} />
      </button>
    </div>
  );
}

export function ChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  function update(k) {
    return (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (form.new_password !== form.confirm_password) {
      setError("New passwords don't match.");
      return;
    }
    if (form.new_password.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await apiPost("/auth/change-password/", form);
      setNotice("Password updated. You're still signed in.");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      if (err instanceof ApiError) setError(err.body?.error || err.message);
      else setError("Couldn't update password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{ marginBottom: 8 }}>Account · Security</div>
          <h1 className="page-title">Change your password.</h1>
          <p className="page-sub" style={{ marginTop: 10, maxWidth: 540 }}>
            Confirm the password you're using now, then pick a new one.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 460 }}>
        {error && <Notice tone="error">{error}</Notice>}
        {notice && <Notice>{notice}</Notice>}

        <form onSubmit={submit} className="card card--flush">
          <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field
              label="Current password"
              type="password"
              value={form.current_password}
              onChange={update("current_password")}
              required
              autoComplete="current-password"
            />
            <Field
              label="New password"
              type="password"
              value={form.new_password}
              onChange={update("new_password")}
              required
              autoComplete="new-password"
            />
            <Field
              label="Confirm new password"
              type="password"
              value={form.confirm_password}
              onChange={update("confirm_password")}
              required
              autoComplete="new-password"
            />
            <div className="dim" style={{ fontSize: 12 }}>
              At least 8 characters. Mix in numbers and a symbol or two.
            </div>
          </div>
          <div className="card-body" style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            borderTop: "1px solid var(--line)",
            paddingTop: 14,
          }}>
            <button type="button" className="btn btn--ghost" onClick={() => navigate("account-settings")}>
              Back to settings
            </button>
            <button type="submit" disabled={submitting} className="btn btn--primary" style={{ opacity: submitting ? 0.6 : 1 }}>
              {submitting ? "Updating…" : "Update password"} <Ico n="arrow" size={14} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
